/* eslint-disable quotes */
import React from "react";
import { degrees, PDFDocument, rgb, StandardFonts } from "pdf-lib";
import { saveAs } from "file-saver";
import statePdf from "./pdfs/template.pdf";
import { getFirestore, doc, getDoc } from "firebase/firestore";

async function fetchUserData(panNumber, assessmentYear) {
  try {
    const db = getFirestore();
    const userRef = doc(db, "itrWorking", panNumber);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User document not found');
    }

    const userData = userDoc.data();
    const yearData = userData[assessmentYear] || {};
    const taxData = yearData.tax || {};
    const taxCalculation = yearData.taxCalculation || {};
    const regime = yearData.regime || 'old';

    // Initialize all values with 0 as default
    const defaults = {
      // Salary Components
      salary: 0,
      conveyance: 0,
      hra: 0,
      otherAllowances: 0,
      travelAllowance: 0,

      // Deductions
      standardDeduction: regime === 'old' ? 50000 : 0,
      professionalTax: 0,
      entertainmentAllowance: 0,

      // Income from other sources
      houseProperty: 0,
      otherIncome: 0,

      // Chapter VIA Deductions
      '80C': 0,
      '80CCD-1': 0,
      '80CCD-1B': 0,
      '80CCD-2': 0,
      '80D': 0,
      '80DD': 0,
      '80DDB': 0,
      '80E': 0,
      '80G': 0,
      '80GG': 0,
      '80U': 0,
      '80EEB': 0,

      // Tax Calculation
      taxOnTotalIncome: 0,
      rebateTotal: 0,
      educationCessTotal: 0,
      reliefUS89: 0,
      tdsTcsAmount: 0
    };

    // Get primary and secondary salary data
    const primarySalary = yearData.incomeFromSalaries?.[0] || {};
    const secondarySalary = yearData.incomeFromSalaries?.[1] || {};

    // Calculate salary components
    const totalSalary = (primarySalary.salary || 0) + (secondarySalary.salary || 0);

    // Calculate allowances
    const primaryAllowances = primarySalary.allowance || {};
    const secondaryAllowances = secondarySalary.allowance || {};

    const totalAllowances = {
      conveyance: (primaryAllowances.conveyance || 0) + (secondaryAllowances.conveyance || 0),
      hra: (primaryAllowances.hra || 0) + (secondaryAllowances.hra || 0),
      travel: (primaryAllowances.travel || 0) + (secondaryAllowances.travel || 0),
      other: (primaryAllowances.other || 0) + (secondaryAllowances.other || 0)
    };

    // Calculate total gross salary (1e in form)
    const totalGrossSalary = totalSalary +
      totalAllowances.conveyance +
      totalAllowances.hra +
      totalAllowances.other;

    // Calculate exempt allowances (2 in form)
    const exemptAllowances = {
      conveyance: Math.min(totalAllowances.conveyance, 1600 * 12), // Example limit for conveyance
      hra: calculateHRAExemption(primarySalary, totalAllowances.hra),
      travel: totalAllowances.travel // Typically fully taxable
    };

    const totalExemptAllowances = exemptAllowances.conveyance + exemptAllowances.hra;

    // Balance after exempt allowances (3 in form)
    const balanceAfterExemptions = totalGrossSalary - totalExemptAllowances;

    // Calculate deductions (4 in form)
    const deductions = {
      standardDeduction: regime === 'old' ? 50000 : 0,
      entertainmentAllowance: regime === 'old' ? Math.min(primarySalary.entertainmentAllowance || 0, 5000) : 0,
      professionalTax: taxCalculation.professionalTax || 0
    };

    const totalDeductions = deductions.standardDeduction +
      deductions.entertainmentAllowance +
      deductions.professionalTax;

    // Income chargeable under salaries (6 in form)
    const incomeChargeableSalaries = balanceAfterExemptions - totalDeductions;

    // Calculate other incomes
    const incomeFromHouseProperty = calculateHousePropertyIncome(yearData.incomeFromHouseProperty || {});
    const incomeFromOtherSources = calculateOtherIncome(yearData.incomeFromOtherSources || {});
    const totalOtherIncome = incomeFromHouseProperty + incomeFromOtherSources;

    // Gross total income (8 in form)
    const grossTotalIncome = incomeChargeableSalaries + totalOtherIncome;

    // Calculate Chapter VIA deductions
    const chapterVIADeductions = calculateChapterVIADeductions(yearData.deductions || {}, regime);

    // Total income (11 in form)
    const totalIncome = Math.max(0, grossTotalIncome - chapterVIADeductions.total);

    // Tax calculation
    const taxDetails = calculateTax(totalIncome, regime);

    // Final tax payable
    const taxPayableAfterCess = taxDetails.taxAfterRebate + taxDetails.educationCess;
    const finalTaxPayable = Math.max(0, taxPayableAfterCess - (taxCalculation.reliefUS89 || 0));
    const balanceTaxPayable = Math.max(0, finalTaxPayable - (yearData.tdsTcsAmount || 0));

    return {
      userObject: {
        year: assessmentYear,
        name: userData?.userdata?.name || '',
        empId: primarySalary?.empId || '',
        addressLine1: primarySalary?.address || '',
        addressLine2: `${primarySalary?.city || ''}, ${primarySalary?.state || ''}`,
        pan: panNumber,
        tan: primarySalary?.tan || ''
      },
      userDeduction: {
        // Basic Info
        regime: regime === 'old' ? 'Old Regime' : 'New Regime',

        // Salary Details
        salary: totalSalary,
        totalgross: totalGrossSalary,
        standardDeduction: deductions.standardDeduction,
        professionalTax: deductions.professionalTax,
        entertainmentAllowance: deductions.entertainmentAllowance,

        // Allowances
        gSalaryC: totalAllowances.conveyance,
        gSalaryD: totalAllowances.hra,
        gSalaryB: totalAllowances.other,
        WATA: totalAllowances.travel,
        HRA: totalAllowances.hra,
        ExcPayRec: totalAllowances.other,
        AllowanceTotal: totalAllowances.conveyance + totalAllowances.hra + totalAllowances.other,
        Balance3: balanceAfterExemptions,

        // Deductions
        Aggregate5: totalDeductions,
        incomeChargableSalaries: incomeChargeableSalaries,

        // Other Income
        houseProperty: incomeFromHouseProperty,
        otherIncome: incomeFromOtherSources,
        total7: totalOtherIncome,

        // Gross Total Income
        grossTotalIncome: grossTotalIncome,

        // Chapter VIA Deductions
        $80c: chapterVIADeductions['80C'],
        $80Total: chapterVIADeductions['80C'],
        $80CCD1B: chapterVIADeductions['80CCD-1B'],
        $80CCD2: chapterVIADeductions['80CCD-2'],
        $80gg: chapterVIADeductions['80GG'],
        $80d: chapterVIADeductions['80D'],
        $80dd: chapterVIADeductions['80DD'],
        $80ddb: chapterVIADeductions['80DDB'],
        $80U: chapterVIADeductions['80U'],
        $80E: chapterVIADeductions['80E'],
        $80G: chapterVIADeductions['80G'],
        $80EEB: chapterVIADeductions['80EEB'],
        Aggregate10: chapterVIADeductions.total,

        // Tax Calculation
        totalIncome: totalIncome,
        taxOnTotalIncome: taxDetails.taxAmount,
        Rebate: taxDetails.rebate,
        EducationCess: taxDetails.educationCess,
        taxPayable: taxDetails.taxAfterRebate,
        reliefUser89: taxCalculation.reliefUS89 || 0,
        taxPayable16: finalTaxPayable,
        taxDeductedAtSource: yearData.tdsTcsAmount || 0,
        taxDeductedByOtherEmployee: 0,
        BalanceTaxPayable: balanceTaxPayable,
        form16BGenerated: yearData.form16BGenerated || false
      }
    };

  } catch (error) {
    console.error('Error fetching user data:', error);
    throw error;
  }
}

// Helper functions
function calculateHRAExemption(salaryData, totalHRA) {
  if (!salaryData || !salaryData.rentPaid) return 0;

  const basicSalary = salaryData.salary || 0;
  const rentPaid = salaryData.rentPaid || 0;
  const cityType = salaryData.cityType || 'nonMetro'; // metro or nonMetro

  // HRA exemption is least of:
  // 1. Actual HRA received
  // 2. Rent paid - 10% of basic salary
  // 3. 50% of basic (metro) or 40% (non-metro)
  const hraExemption = Math.min(
    totalHRA,
    rentPaid - (0.1 * basicSalary),
    (cityType === 'metro' ? 0.5 : 0.4) * basicSalary
  );

  return Math.max(0, hraExemption);
}

function calculateHousePropertyIncome(housePropertyData) {
  let netIncome = 0;

  // For let out property
  if (housePropertyData.LetOut) {
    netIncome += (housePropertyData.LetOut.grossAnnual || 0) -
      (housePropertyData.LetOut.municipalTaxes || 0) -
      (housePropertyData.LetOut.standardDeduction || 0) -
      (housePropertyData.LetOut.interestOnBorrowedCapital || 0);
  }

  // For self occupied property (loss)
  if (housePropertyData.SelfOccupied) {
    netIncome -= Math.min(200000, housePropertyData.SelfOccupied.interestOnBorrowedCapital || 0);
  }

  return netIncome;
}

function calculateOtherIncome(otherIncomeData) {
  let total = 0;

  // Interest income
  if (otherIncomeData.interestIncome) {
    total += (otherIncomeData.interestIncome.InterestOnSavingsAccount || 0) +
      (otherIncomeData.interestIncome.InterestOnTimeDeposits || 0) +
      (otherIncomeData.interestIncome.otherInterest || 0);
  }

  // Dividends
  total += otherIncomeData.dividends || 0;

  // Other sources
  total += otherIncomeData.other || 0;

  return total;
}

function calculateChapterVIADeductions(deductionsData, regime) {
  const result = {
    '80C': 0,
    '80CCD-1': 0,
    '80CCD-1B': 0,
    '80CCD-2': 0,
    '80D': 0,
    '80DD': 0,
    '80DDB': 0,
    '80E': 0,
    '80G': 0,
    '80GG': 0,
    '80U': 0,
    '80EEB': 0,
    total: 0
  };

  if (regime === 'old') {
    // Section 80C (capped at 1.5 lakh)
    result['80C'] = Math.min(
      150000,
      (deductionsData['80C']?.gpf || 0) +
      (deductionsData['80C']?.homeLoan || 0) +
      (deductionsData['80C']?.lic || 0) +
      (deductionsData['80C']?.tuitionFee || 0) +
      (deductionsData['80CCD']?.['80CCD-1'] || 0)
    );

    // Section 80CCD(1B) - NPS (capped at 50k)
    result['80CCD-1B'] = Math.min(50000, deductionsData['80CCD']?.['80CCD-1B'] || 0);

    // Other sections
    result['80CCD-2'] = deductionsData['80CCD']?.['80CCD-2'] || 0; // Employer's NPS contribution
    result['80D'] = (deductionsData['80D']?.above60 || 0) + (deductionsData['80D']?.below60 || 0);
    result['80DD'] = deductionsData['80DD'] || 0;
    result['80DDB'] = deductionsData['80DDB']?.amount || 0;
    result['80G'] = deductionsData['80G'] || 0;
    result['80GG'] = deductionsData['80GG'] || 0;
    result['80U'] = deductionsData['80U'] || 0;

    result.total = result['80C'] + result['80CCD-1B'] + result['80CCD-2'] +
      result['80D'] + result['80DD'] + result['80DDB'] +
      result['80G'] + result['80GG'] + result['80U'];
  } else {
    // New regime only allows 80CCD(2)
    result['80CCD-2'] = deductionsData['80CCD']?.['80CCD-2'] || 0;
    result.total = result['80CCD-2'];
  }

  return result;
}

function calculateTax(taxableIncome, regime) {
  let taxAmount = 0;
  let rebate = 0;

  if (regime === 'old') {
    // Old regime tax slabs (FY 2023-24 example)
    if (taxableIncome <= 250000) {
      taxAmount = 0;
    } else if (taxableIncome <= 500000) {
      taxAmount = (taxableIncome - 250000) * 0.05;
    } else if (taxableIncome <= 1000000) {
      taxAmount = 12500 + (taxableIncome - 500000) * 0.2;
    } else {
      taxAmount = 112500 + (taxableIncome - 1000000) * 0.3;
    }

    // Rebate under 87A
    if (taxableIncome <= 500000) {
      rebate = Math.min(12500, taxAmount);
      taxAmount = Math.max(0, taxAmount - rebate);
    }
  } else {
    // New regime tax slabs (FY 2023-24 example)
    if (taxableIncome <= 250000) {
      taxAmount = 0;
    } else if (taxableIncome <= 500000) {
      taxAmount = (taxableIncome - 250000) * 0.05;
    } else if (taxableIncome <= 750000) {
      taxAmount = 12500 + (taxableIncome - 500000) * 0.1;
    } else if (taxableIncome <= 1000000) {
      taxAmount = 37500 + (taxableIncome - 750000) * 0.15;
    } else if (taxableIncome <= 1250000) {
      taxAmount = 75000 + (taxableIncome - 1000000) * 0.2;
    } else if (taxableIncome <= 1500000) {
      taxAmount = 125000 + (taxableIncome - 1250000) * 0.25;
    } else {
      taxAmount = 187500 + (taxableIncome - 1500000) * 0.3;
    }

    // Rebate under 87A for new regime
    if (taxableIncome <= 700000) {
      rebate = Math.min(25000, taxAmount);
      taxAmount = Math.max(0, taxAmount - rebate);
    }
  }

  // Education cess (4%)
  const educationCess = taxAmount * 0.04;

  return {
    taxAmount: taxAmount,
    rebate: rebate,
    educationCess: educationCess,
    taxAfterRebate: taxAmount + educationCess
  };
}

async function modifyPdf(panNumber, assessmentYear) {
  const { userObject, userDeduction } = await fetchUserData(panNumber, assessmentYear);

  // Load the PDF template
  const url = statePdf;
  const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const helveticaBoldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const pages = pdfDoc.getPages();
  const firstPage = pages[0];

  // PDF coordinates configuration
  let zeroth_column = 245;
  let first_column = 330;
  let second_column = 410;
  let third_column = 490;
  let dist = 13;
  let base = 728;
  const regime = userDeduction.regime;

  // -------------------- Section 0 (Header) Starts -------------------------------------
  base = 805;
  firstPage.drawText(userObject.year + " " + regime, {
    x: 46,
    y: base,
    size: 9,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });

  base = 789;
  firstPage.drawText(userObject.name, {
    x: 50,
    y: base,
    size: 7,
    font: helveticaFont,
    color: rgb(0, 0, 0),
  });

  base = 790;
  firstPage.drawText(userObject.empId, {
    x: 245,
    y: base,
    size: 7,
    font: helveticaFont,
    color: rgb(0, 0, 0),
  });

  base = 780;
  firstPage.drawText(userObject.addressLine1, {
    x: 398,
    y: base,
    size: 7,
    font: helveticaFont,
    color: rgb(0, 0, 0),
  });

  base = 771;
  firstPage.drawText(userObject.addressLine2, {
    x: 398,
    y: base,
    size: 7,
    font: helveticaFont,
    color: rgb(0, 0, 0),
  });

  base = 778;
  firstPage.drawText(userObject.pan, {
    x: 40,
    y: base,
    size: 7,
    font: helveticaFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(userObject.tan, {
    x: 270,
    y: base,
    size: 7,
    font: helveticaFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 0 (Header) Ends -------------------------------------

  // -------------------- Section 1 (Gross Salary) Starts -------------------------------------
  base = 728;
  firstPage.drawText(`Rs ${userDeduction?.salary}`, {
    x: first_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.gSalaryC}`, {
    x: first_column,
    y: base - dist * 1,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.gSalaryD}`, {
    x: first_column,
    y: base - dist * 2,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.gSalaryB}`, {
    x: first_column,
    y: base - dist * 3,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.totalgross}`, {
    x: second_column,
    y: base - dist * 4,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 1(Gross Salary) Ends -------------------------------------

  // -------------------- Section 2 (Less) Starts -------------------------------------
  base = 636;
  firstPage.drawText(`Rs ${userDeduction.WATA}`, {
    x: zeroth_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.HRA}`, {
    x: zeroth_column,
    y: base - dist * 1,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.ExcPayRec}`, {
    x: zeroth_column,
    y: base - dist * 2,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.AllowanceTotal}`, {
    x: second_column,
    y: base - dist * 3,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 2 (Less) Ends  -------------------------------------

  // -------------------- Section 3 (Balance) Starts -------------------------------------
  base = 584;
  firstPage.drawText(`Rs ${userDeduction.Balance3}`, {
    x: second_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 3 (Balance) Ends -------------------------------------

  // -------------------- Section 4 (Deduction) Starts -------------------------------------
  base = 560;
  firstPage.drawText(`Rs ${userDeduction.standardDeduction}`, {
    x: first_column,
    y: base - dist * 0,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${0}`, { // entertainmentAllowance
    x: first_column,
    y: base - dist * 1,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.professionalTax}`, {
    x: first_column,
    y: base - dist * 2,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 4 (Deduction) Ends -------------------------------------

  // -------------------- Section 5 (Aggregate) Starts -------------------------------------
  base = 519;
  firstPage.drawText(`Rs ${isNaN(userDeduction.Aggregate5) ? 0 : userDeduction.Aggregate5}`, {
    x: second_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 5 (Aggregate) Ends -------------------------------------

  // -------------------- Section 6 (Income Chargable) Starts -------------------------------------
  base = 505;
  firstPage.drawText(`Rs ${userDeduction.incomeChargableSalaries}`, {
    x: third_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 6 (Income Chargable) Ends -------------------------------------

  // -------------------- Section 7 (Other Income) Starts -------------------------------------
  base = 466;
  firstPage.drawText(`Rs ${userDeduction.houseProperty}`, {
    x: zeroth_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.otherIncome}`, {
    x: zeroth_column,
    y: base - dist * 1,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  base = 442;
  firstPage.drawText(`Rs ${userDeduction.total7}`, {
    x: second_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 7 (Other Income) Ends -------------------------------------

  // -------------------- Section 8 (Gross Total Income) Starts -------------------------------------
  base = 427;
  firstPage.drawText(`Rs ${userDeduction.grossTotalIncome}`, {
    x: third_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 8 (Gross Total Income) Ends -------------------------------------

  // -------------------- Section 9 (Deduction under Chapter VIA) Starts -------------------------------------
  base = 390;
  // 9A
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction?.$80c}`, {
    x: second_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction?.$80Total}`, {
    x: third_column,
    y: base,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${0}`, { //80CCC
    x: second_column,
    y: base - dist * 1,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${0}`, { //80 CCD
    x: second_column,
    y: base - dist * 2,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });

  base = 337;
  // 9B First Column -------------
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80CCD1B}`, {
    x: first_column,
    y: base - dist * 0,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.$80CCD2}`, {
    x: first_column,
    y: base - dist * 1,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80gg}`, {
    x: first_column,
    y: base - dist * 2,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80d}`, {
    x: first_column,
    y: base - dist * 3,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80dd}`, {
    x: first_column,
    y: base - dist * 4,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80ddb}`, {
    x: first_column,
    y: base - dist * 5,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80U}`, {
    x: first_column,
    y: base - dist * 6,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80E}`, {
    x: first_column,
    y: base - dist * 7,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80G}`, {
    x: first_column,
    y: base - dist * 8,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80EEB}`, {
    x: first_column,
    y: base - dist * 9,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });

  // 9B Second Column -----------
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80CCD1B}`, {
    x: second_column,
    y: base - dist * 0,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.$80CCD2}`, {
    x: second_column,
    y: base - dist * 1,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80gg}`, {
    x: second_column,
    y: base - dist * 2,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80d}`, {
    x: second_column,
    y: base - dist * 3,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80dd}`, {
    x: second_column,
    y: base - dist * 4,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80ddb}`, {
    x: second_column,
    y: base - dist * 5,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80U}`, {
    x: second_column,
    y: base - dist * 6,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80E}`, {
    x: second_column,
    y: base - dist * 7,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80G}`, {
    x: second_column,
    y: base - dist * 8,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80EEB}`, {
    x: second_column,
    y: base - dist * 9,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });

  // 9B Third Column --------------
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80CCD1B}`, {
    x: third_column,
    y: base - dist * 0,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.$80CCD2}`, {
    x: third_column,
    y: base - dist * 1,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80gg}`, {
    x: third_column,
    y: base - dist * 2,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80d}`, {
    x: third_column,
    y: base - dist * 3,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80dd}`, {
    x: third_column,
    y: base - dist * 4,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80ddb}`, {
    x: third_column,
    y: base - dist * 5,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80U}`, {
    x: third_column,
    y: base - dist * 6,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80E}`, {
    x: third_column,
    y: base - dist * 7,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80G}`, {
    x: third_column,
    y: base - dist * 8,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${regime === "New Regime" ? 0 : userDeduction.$80EEB}`, {
    x: third_column,
    y: base - dist * 9,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 9 (Deduction under Chapter VIA) Ends -------------------------------------

  // -------------------- Section 10 - 18 (Other) Starts -------------------------------------
  base = 205;
  firstPage.drawText(`Rs ${regime === "New Regime" ? userDeduction.$80CCD2 : userDeduction.Aggregate10}`, {
    x: third_column,
    y: base - dist * 0,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.totalIncome}`, {
    x: third_column,
    y: base - dist * 1,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.taxOnTotalIncome}`, {
    x: third_column,
    y: base - dist * 2,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.Rebate}`, {
    x: third_column,
    y: base - dist * 3,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.EducationCess}`, {
    x: third_column,
    y: base - dist * 4,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.taxPayable}`, {
    x: third_column,
    y: base - dist * 5,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.reliefUser89}`, {
    x: third_column,
    y: base - dist * 6,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.taxPayable16}`, {
    x: third_column,
    y: base - dist * 7,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.taxDeductedAtSource}`, {
    x: second_column,
    y: base - dist * 8,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${0}`, { // taxDeductedByOtherEmployee
    x: second_column,
    y: base - dist * 9,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  firstPage.drawText(`Rs ${userDeduction.BalanceTaxPayable}`, {
    x: third_column,
    y: base - dist * 10,
    size: 7,
    font: helveticaBoldFont,
    color: rgb(0, 0, 0),
  });
  // -------------------- Section 10 - 18 (Other) Ends -------------------------------------

  const pdfBytes = await pdfDoc.save();
  const pdfBlob = new Blob([pdfBytes], { type: `application/pdf` });
  saveAs(pdfBlob, `TS-Form16.pdf`);
}

export default modifyPdf;